/** @jsxImportSource @emotion/react */

import {
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import Modal from 'react-modal'
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace'
import { useModalScroll } from 'src/hooks/useModalScroll'

Modal.setAppElement('#root')

interface Props {
  children: React.ReactNode | EmotionJSX.Element
  shouldCloseOnOverlayClick?: boolean
  shouldCloseOnEscPreventDefault?: boolean
}

type NewModal = {
  openModal: () => void
  closeModal: () => void
}

const modalRef = createRef<NewModal>()

const NewModal = forwardRef(
  (
    {
      children,
      shouldCloseOnOverlayClick = true,
      shouldCloseOnEscPreventDefault = true,
    }: Props,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    // Shares the app-wide scroll lock rather than writing
    // `documentElement.style.overflowY` directly. Doing it directly meant this
    // component could clear the lock while another modal was still open —
    // `useModalScroll` reference-counts it, so the page stays locked until the
    // last holder releases.
    const { openModal: lockScroll, closeModal: unlockScroll } = useModalScroll()

    const openModal = useCallback(() => {
      setIsOpen(true)
      lockScroll()
    }, [lockScroll])

    const closeModal = useCallback(() => {
      setIsOpen(false)
      unlockScroll()
    }, [unlockScroll])

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Escape') {
          if (!shouldCloseOnEscPreventDefault) {
            return
          }
          closeModal()
        }
      },
      [closeModal, shouldCloseOnEscPreventDefault]
    )

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
      // The scroll lock is NOT released here. This cleanup re-runs whenever
      // `handleKeyDown` changes — not just on unmount — so clearing the style
      // here unlocked the page mid-modal. `useModalScroll` releases this
      // component's lock on unmount by itself.
    }, [handleKeyDown])

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }))

    return (
      <Modal
        shouldCloseOnEsc={false}
        isOpen={isOpen}
        closeTimeoutMS={300}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            transition: 'all 0.3s ease',
            backgroundColor: 'transparent',
            border: 0,
          },
          overlay: {
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10000,
            transition: 'all 0.3s ease',
          },
        }}
        contentLabel='SportNinja Modal'
      >
        {children}
      </Modal>
    )
  }
)

NewModal.displayName = 'NewModal'

export { NewModal, modalRef }
